import { useEffect, useMemo, useState, type MutableRefObject } from 'react';
import { Alert, useTheme } from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Appearance, Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { BlockLoading, useToastr } from '@farmshare/ui-components';
import { environment } from 'environments/environment';

/** Give Stripe.js this long to initialise before treating it as unavailable. */
const STRIPE_INIT_TIMEOUT_MS = 15000;

/** Confirms the payment; resolves true on success, false (with a toast) otherwise. */
export type DepositConfirm = () => Promise<boolean>;

interface Props {
  clientSecret: string;
  /** The vendor's connected account — Stripe.js must be scoped to it. */
  connectedAccountId: string;
  /** The parent registers/reads the confirm handler here to drive its own button. */
  confirmRef: MutableRefObject<DepositConfirm | null>;
  onReadyChange?: (ready: boolean) => void;
  /**
   * Called if Stripe.js can't initialise (blocked/offline/timeout) so the host
   * can clear any "ready" state and avoid dead-ending on a step that will never
   * become payable.
   */
  onUnavailable?: () => void;
}

/**
 * Self-rendered Stripe Elements card fields (card only), themed to the app, with
 * the confirm action exposed via `confirmRef` so the host (a modal button or a
 * multi-step "Next") can trigger payment. Shared by the deposit modal and the
 * in-flow booking deposit step.
 */
export function DepositPaymentFields({
  clientSecret,
  connectedAccountId,
  confirmRef,
  onReadyChange,
  onUnavailable,
}: Props) {
  const theme = useTheme();

  const stripePromise = useMemo<Promise<Stripe | null>>(
    () =>
      loadStripe(environment.stripeKey, { stripeAccount: connectedAccountId }),
    [connectedAccountId],
  );

  // Detect a Stripe.js init failure (blocked script / offline / timeout) up
  // front, so we render a clear fallback instead of mounting <Elements> with a
  // promise that never resolves — which leaves the step stuck and, on some
  // paths, white-screens via the error boundary.
  const [stripeStatus, setStripeStatus] = useState<
    'loading' | 'ready' | 'error'
  >('loading');
  useEffect(() => {
    let active = true;
    const timeout = setTimeout(() => {
      if (active) {
        setStripeStatus((prev) => (prev === 'loading' ? 'error' : prev));
        onUnavailable?.();
      }
    }, STRIPE_INIT_TIMEOUT_MS);
    stripePromise
      .then((stripe) => {
        if (!active) return;
        if (stripe) {
          setStripeStatus('ready');
        } else {
          setStripeStatus('error');
          onUnavailable?.();
        }
      })
      .catch(() => {
        if (!active) return;
        setStripeStatus('error');
        onUnavailable?.();
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripePromise]);

  // Match Stripe Elements to the app's MUI theme (colors, font, radius, focus).
  const appearance = useMemo<Appearance>(
    () => ({
      theme: 'stripe',
      variables: {
        colorPrimary: theme.palette.primary.main,
        colorText: theme.palette.text.primary,
        colorTextSecondary: theme.palette.text.secondary,
        colorDanger: theme.palette.error.main,
        colorBackground: theme.palette.background.paper,
        fontFamily: String(theme.typography.fontFamily),
        borderRadius: `${theme.shape.borderRadius}px`,
        spacingUnit: '4px',
        fontSizeBase: '15px',
      },
      rules: {
        '.Input': {
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          padding: '10px 12px',
        },
        '.Input:focus': {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        },
        '.Input--invalid': {
          border: `1px solid ${theme.palette.error.main}`,
        },
        '.Label': {
          fontWeight: '600',
          color: theme.palette.text.secondary,
          marginBottom: '6px',
        },
        '.Tab': {
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        },
        '.Tab:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '.Tab--selected': {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        },
      },
    }),
    [theme],
  );

  if (stripeStatus === 'loading') return <BlockLoading />;
  if (stripeStatus === 'error') {
    return (
      <Alert severity="error">
        We couldn&apos;t load the secure payment form. Please check your
        connection or disable any ad/script blockers and try again.
      </Alert>
    );
  }

  // data-cs-private suppresses this whole card-entry region from Contentsquare
  // session replay (see lib/contentsquare.ts setPIISelectors). Stripe's
  // PaymentElement is a cross-origin iframe CS can't record anyway, but this
  // masks any card/billing detail we render around it as defense-in-depth.
  return (
    <div data-cs-private>
      <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
        <Fields confirmRef={confirmRef} onReadyChange={onReadyChange} />
      </Elements>
    </div>
  );
}

function Fields({
  confirmRef,
  onReadyChange,
}: {
  confirmRef: MutableRefObject<DepositConfirm | null>;
  onReadyChange?: (ready: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { push } = useToastr();

  useEffect(() => {
    confirmRef.current = async () => {
      if (!stripe || !elements) return false;
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      // Already confirmed on an earlier attempt (the booking failed after the
      // card step and the customer is retrying) — the card doesn't need
      // confirming again, and Stripe refuses to.
      const alreadyConfirmed =
        error?.code === 'payment_intent_unexpected_state' &&
        (error.payment_intent?.status === 'requires_capture' ||
          error.payment_intent?.status === 'succeeded');
      if (alreadyConfirmed) return true;
      if (error) {
        push({
          title: 'Payment failed',
          body: error.message ?? 'Please check your card details.',
          severity: 'error',
        });
        return false;
      }
      // `requires_capture`: an authorize-only deposit (self-serve booking) —
      // the card is held and the server charges it once the job exists.
      if (
        paymentIntent?.status === 'succeeded' ||
        paymentIntent?.status === 'requires_capture'
      ) {
        return true;
      }
      push({
        title: 'Payment not completed',
        body: `Payment status: ${paymentIntent?.status ?? 'unknown'}.`,
        severity: 'warning',
      });
      return false;
    };
    return () => {
      confirmRef.current = null;
    };
  }, [stripe, elements, push, confirmRef]);

  return <PaymentElement onReady={() => onReadyChange?.(true)} />;
}
